import React, { useState, useEffect, useRef } from "react";
import assets from "../../../assets";
import { useApi } from "../../../hooks/use-api";
import { useQuestionBankHandler } from "../handler";
import { COOKIE_ACCESS_MAIL } from "../../../utils/storage-constants";
import "./style.sass";

export const ImportPopup = ({ onClose, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [testName, setTestName] = useState("");
  const [noOfQuestions, setNoOfQuestions] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testList, setTestList] = useState([]);
  const [testDropdownOpen, setTestDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);

  const dropdownRef = useRef(null);
  const api = useApi();
  const { uploadFile, fetchTeacherTestList } = useQuestionBankHandler(api);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setTestDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadTests = async () => {
      setTestLoading(true);
      try {
        const res = await fetchTeacherTestList({ page: 1, pageSize: 50 });
        if (isMounted && res) {
          const list = Array.isArray(res)
            ? res
            : Array.isArray(res.tests)
              ? res.tests
              : Array.isArray(res.active_tests)
                ? res.active_tests
                : Array.isArray(res.data)
                  ? res.data
                  : [];
          if (list.length > 0) {
            setTestList(list);
          }
        }
      } catch (err) {
        console.error("Error loading test list for dropdown:", err);
      } finally {
        if (isMounted) setTestLoading(false);
      }
    };

    loadTests();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleFile = (selectedFile) => {
    if (
      selectedFile &&
      (selectedFile.name.endsWith(".xlsx") ||
        selectedFile.name.endsWith(".xls") ||
        selectedFile.name.endsWith(".csv") ||
        selectedFile.name.endsWith(".pdf"))
    ) {
      setFile(selectedFile);
      setError(null);
    } else {
      setError("Please select a valid file (.pdf, .xlsx, .xls, .csv)");
    }
  };

  const handleChange = (e) => handleFile(e.target.files[0]);

  const isValidTestName = testList.some((t) => {
    const name = t.test_name || t.testName || "";
    return name.trim().toLowerCase() === (testName || "").trim().toLowerCase();
  });

  const isFormValid = isValidTestName && file && noOfQuestions && noOfQuestions.toString().trim() !== "";

  const handleUpload = async () => {
    if (!testName || !testName.trim() || !isValidTestName) {
      setError("Please select a valid test name from the dropdown.");
      return;
    }
    if (!noOfQuestions || !noOfQuestions.toString().trim()) {
      setError("Please enter the number of questions.");
      return;
    }
    if (!file) {
      setError("Please select a file to upload.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await uploadFile(file, { testName: testName.trim(), noOfQuestions });
      onUploadSuccess(res || {}, { testName: testName.trim(), noOfQuestions });
      onClose();
    } catch (err) {
      const errorDetail = err?.response?.data?.detail || err?.message || "Failed to upload file. Please try a different test name.";
      setError(errorDetail);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <>
      {loading && (
        <div className="kb_processing_overlay">
          <div className="kb_processing_box">
            <div className="kb_spinner">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p className="kb_processing_text">
              Processing your content and updating the question bank. Please stay on this page. Once completed, you can conduct a test using Test Management.
            </p>
          </div>
        </div>
      )}
      <div className="import_popup_overlay" onClick={onClose}>
        <div className="popup_box" onClick={(e) => e.stopPropagation()}>
          <button className="close_btn" onClick={onClose} aria-label="Close popup">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <h2>Import Questions</h2>
          <p className="popup_subtitle">Upload an Excel file (.xlsx, .xls) to import questions</p>

          {error && (
            <div
              style={{
                backgroundColor: "#fee2e2",
                color: "#991b1b",
                padding: "10px 14px",
                borderRadius: "8px",
                marginBottom: "16px",
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <div className={`qb_import_form_group_top test_group ${testDropdownOpen ? "is_open" : ""}`} ref={dropdownRef}>
            <label>Test Name</label>
            <div className="qb_import_combobox_container">
              <input
                className="qb_import_combobox_input"
                type="text"
                placeholder={testLoading ? "Loading test names..." : "Select or search test name..."}
                value={testName}
                onChange={(e) => {
                  setTestName(e.target.value);
                  setTestDropdownOpen(true);
                  setError(null);
                }}
                onFocus={() => {
                  setTestDropdownOpen(true);
                }}
                disabled={testLoading}
              />
              <svg
                className={`qb_import_combobox_arrow ${testDropdownOpen ? "rotated" : ""}`}
                onClick={() => {
                  setTestDropdownOpen((prev) => !prev);
                }}
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6B7280"
                strokeWidth="2.2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>

              {testDropdownOpen && (
                <div className="qb_import_combobox_menu">
                  <div className="qb_import_combobox_options">
                    {testList
                      .filter((t) => {
                        const name = t.test_name || t.testName || "";
                        return name.toLowerCase().includes((testName || "").toLowerCase());
                      })
                      .map((t, idx) => {
                        const name = t.test_name || t.testName || "";
                        if (!name) return null;
                        const isSelected = testName === name;
                        return (
                          <div
                            key={t.test_id || idx}
                            className={`qb_import_combobox_item ${isSelected ? "selected" : ""}`}
                            onClick={() => {
                              setTestName(name);
                              setTestDropdownOpen(false);
                              setError(null);
                            }}
                          >
                            <span>{name}</span>
                            {isSelected && (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </div>
                        );
                      })}
                    {testList.filter((t) => (t.test_name || t.testName || "").toLowerCase().includes((testName || "").toLowerCase())).length === 0 && !testLoading && (
                      <div className="qb_import_combobox_empty">No matching test names</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form_group">
            <label>No. of Questions</label>
            <input
              type="number"
              placeholder="128"
              value={noOfQuestions}
              onChange={(e) => setNoOfQuestions(e.target.value)}
            />
          </div>


          <div
            className={`upload_area ${dragActive ? "active" : ""}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById("fileInput").click()}
          >
            <input
              id="fileInput"
              type="file"
              hidden
              accept=".xlsx, .xls, .csv, .pdf"
              onChange={handleChange}
            />
            <div className="upload_icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            {!file ? (
              <>
                <div className="upload_title">Question Bank</div>
                <div className="supported_text">Supported format: .pdf, .xlsx, .xls, .csv — max 10 MB</div>
              </>
            ) : (
              <>
                <div className="upload_title" style={{ color: "#612EF7" }}>{file.name}</div>
                <div className="supported_text">Click or drag to change file</div>
              </>
            )}
          </div>

          <div className="popup_actions">
            <button className="cancel_btn" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              className={`upload_btn ${isFormValid ? "active" : ""}`}
              onClick={handleUpload}
              disabled={loading || !isFormValid}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              {loading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
